'use client';

import SvgChevronRight from '@bazaar-marketplace/assets/chevron-right';
import { SvgTon } from '@bazaar-marketplace/assets/ton';
import { cn } from '@bazaar-marketplace/utils';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '~/components';
import { TgStarIcon } from '~/components/icons/tg-star-icon';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '~/components/ui/sheet';

type PaymentMethod = 'telegram_stars' | 'ton_wallet';

interface SelectDirectSalePaymentMethodProps {
  onChange: (method: PaymentMethod) => void;
  currentMethod: PaymentMethod;
  starPricePerPack: string | null;
  tonPricePerPack: string | null;
}

const paymentMethodIcons = {
  telegram_stars: TgStarIcon,
  ton_wallet: SvgTon,
};

const paymentMethodLabels = {
  telegram_stars: 'Stars',
  ton_wallet: 'GRAM',
};

export function SelectDirectSalePaymentMethod({
  onChange,
  currentMethod,
  starPricePerPack,
  tonPricePerPack,
}: SelectDirectSalePaymentMethodProps) {
  const t = useTranslations('directSale');
  const [open, setOpen] = useState(false);

  const handlePaymentMethodSelect = (method: PaymentMethod) => {
    onChange(method);
  };

  function onSelectClick() {
    setOpen(false);
  }

  const CurrentIcon = paymentMethodIcons[currentMethod];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button className="w-full border rounded-xl border-white/25 gap-2 p-2 items-center" variant="dark">
          <div className="rounded-full bg-white size-8 shrink-0 flex items-center justify-center">
            <CurrentIcon className="w-5 h-5 text-black" />
          </div>
          <span className="text-[18px] text-white font-bold">{paymentMethodLabels[currentMethod]}</span>
          <SvgChevronRight className="text-white size-4 rotate-90" />
        </Button>
      </SheetTrigger>
      <SheetContent className="bg-black pb-10" roundedClassName="rounded-t-3xl">
        <SheetHeader className="mb-6">
          <SheetTitle>
            <div className="text-[24px] text-white font-bold">{t('selectSheet.title')}</div>
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-3">
          {(['telegram_stars', 'ton_wallet'] as PaymentMethod[]).map((method) => {
            const Icon = paymentMethodIcons[method];
            const isSelected = method === currentMethod;
            const isUnavailable = method === 'ton_wallet' ? !tonPricePerPack : !starPricePerPack;

            function getClassNames() {
              if (isSelected) {
                return {
                  button: 'bg-white/25 border-white/10',
                  iconBackground: 'bg-white',
                  iconForeground: 'text-black',
                };
              }
              if (isUnavailable) {
                return {
                  button: 'bg-white/5 border-white/10 opacity-50 cursor-not-allowed',
                  iconBackground: 'bg-white/10',
                  iconForeground: 'opacity-50 text-black invert',
                };
              }
              return {
                button: 'bg-white/10 border-white/25 opacity-30',
                iconBackground: 'bg-white/20',
                iconForeground: 'text-black invert',
              };
            }

            const classNames = getClassNames();

            return (
              <button
                key={method}
                className={`w-full p-4 rounded-2xl border transition-all ${classNames.button}`}
                disabled={isUnavailable}
                onClick={() => {
                  if (!isUnavailable) {
                    handlePaymentMethodSelect(method);
                  }
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'rounded-full size-8 shrink-0 flex items-center justify-center',
                        classNames.iconBackground,
                      )}
                    >
                      <Icon className={cn('w-5 h-5', classNames.iconForeground)} />
                    </div>
                    <div className="text-left">
                      <div className={`font-bold text-[18px] ${isUnavailable ? 'text-white/50' : 'text-white'}`}>
                        {paymentMethodLabels[method]}
                        {isUnavailable ? <div className="text-[12px] font-normal mt-1">Not available</div> : null}
                      </div>
                    </div>
                  </div>
                  <div>
                    {method === 'telegram_stars' && starPricePerPack ? (
                      <div className="font-bold text-white text-[18px]">{starPricePerPack} stars</div>
                    ) : null}
                    {method === 'ton_wallet' && tonPricePerPack ? (
                      <div className="font-bold text-white text-[18px]">{tonPricePerPack} GRAM</div>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <Button className="mt-10" variant="rainbow" onClick={onSelectClick}>
          {t('selectSheet.select')}
        </Button>
      </SheetContent>
    </Sheet>
  );
}
